import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { StandaloneFormattedInput } from "@/components/shared";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * RangeInputField component for filtering by a range of values
 *
 * This component provides two inputs for specifying a range (from/to)
 * and is designed to be used in filter interfaces.
 *
 * @component
 * @example
 * <RangeInputField
 *   label="Revenue"
 *   fromValue={fromRevenue}
 *   toValue={toRevenue}
 *   onFromChange={setFromRevenue}
 *   onToChange={setToRevenue}
 *   placeholder="Revenue"
 *   useFormattedInput
 *   style="currency"
 *   currency="EUR"
 * />
 */
interface RangeInputFieldProps {
  /** The label for the range input */
  label: string;
  /** The current 'from' value */
  fromValue: string | number;
  /** The current 'to' value */
  toValue: string | number;
  /** Callback when 'from' value changes */
  onFromChange: (_value: string | number) => void;
  /** Callback when 'to' value changes */
  onToChange: (_value: string | number) => void;
  /** Placeholder text for the inputs */
  placeholder?: string;
  /** Whether to use formatted input instead of regular Input */
  useFormattedInput?: boolean;
  /** Style for formatted input: "currency" or "decimal" */
  style?: "currency" | "decimal";
  /** Currency code for formatted input */
  currency?: string;
  /** Additional suffix for formatted input */
  additionalSuffix?: string;
  /** Whether the inputs are disabled */
  disabled?: boolean;
  /** Form control for FormattedNumberInputField (only needed if using with react-hook-form) */
  control?: any;
  /** CSS class name for the input fields */
  className?: string;
}

/**
 * A component for inputting a range of values (from/to)
 */
const RangeInputField = ({
  label,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  placeholder = "",
  useFormattedInput = false,
  style = "decimal",
  currency = "EUR",
  additionalSuffix = "",
  disabled = false,
  className = "",
}: RangeInputFieldProps) => {

  // Hooks
  const { t } = useTranslation(["common", "components"]);

  // Local state for regular input values
  const [fromInputValue, setFromInputValue] = useState<string>(fromValue != null ? fromValue.toString() : "");
  const [toInputValue, setToInputValue] = useState<string>(toValue != null ? toValue.toString() : "");

  // Update local state when props change
  useEffect(() => {
    setFromInputValue(fromValue != null ? fromValue.toString() : "");
  }, [fromValue]);

  useEffect(() => {
    setToInputValue(toValue != null ? toValue.toString() : "");
  }, [toValue]);

  // Handle regular input changes
  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setFromInputValue(value);
    onFromChange(value);
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setToInputValue(value);
    onToChange(value);
  };

  return (
    <div className="gap-2">
      <div className="shrink-0 mb-2">
        <Label htmlFor={`${label.toLowerCase()}-from`} className="text-sm font-medium">
          {label}
        </Label>
      </div>
      <div className="flex items-center gap-2 grow">
        <div className="flex-1">
          {useFormattedInput ? (
          // Use StandaloneFormattedInput if no control is provided
            <StandaloneFormattedInput
              className={className}
              value={fromValue}
              onChange={onFromChange}
              name={`from_${label.toLowerCase()}`}
              id={`${label.toLowerCase()}-from`}
              disabled={disabled}
              style={style}
              currency={currency}
              additionalSuffix={additionalSuffix}
              placeholder={`${t("common:min")} ${placeholder}`}
              inputHeight="h-9"
            />
          ) : (
            <Input
              id={`${label.toLowerCase()}-from`}
              type="text"
              value={fromInputValue}
              onChange={handleFromChange}
              placeholder={`${t("common:min")} ${placeholder}`}
              className={cn("h-9", className)}
              disabled={disabled}
            />
          )}
        </div>
        <div className="shrink-0 text-sm text-gray-500">{t("common:to")}</div>
        <div className="flex-1">
          {useFormattedInput ? (
          // Use StandaloneFormattedInput if no control is provided
            <StandaloneFormattedInput
              className={className}
              value={toValue}
              onChange={onToChange}
              name={`to_${label.toLowerCase()}`}
              id={`${label.toLowerCase()}-to`}
              disabled={disabled}
              style={style}
              currency={currency}
              additionalSuffix={additionalSuffix}
              placeholder={`${t("common:max")} ${placeholder}`}
              inputHeight="h-9"
            />
          ) : (
            <Input
              id={`${label.toLowerCase()}-to`}
              type="text"
              value={toInputValue}
              onChange={handleToChange}
              placeholder={`${t("common:max")} ${placeholder}`}
              className={cn("h-9", className)}
              disabled={disabled}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RangeInputField;
